import { carriesExif, type Container, sniff, stripMetadata, writeExifBlock } from "./embed";
import { countEntries, emptyExif, type Exif, type ExifEntry, findEntry, IFD_NAMES, setEntry, writeExif } from "./exif";
import { coordinate, DATE_FIELDS, DATE_PATTERN, EDITABLE, ORIENTATION_TAG, plainText, readComment, toCoordinate, writeComment } from "./tags";

// What somebody typed into the metadata card, and how it becomes a directory again. The fields are held apart from
// the EXIF they came out of so an edit can be taken back: the picture keeps the block it arrived with until
// something is actually saved, and clearing a box is a removal rather than a field with nothing in it.

export interface Edits {
  fields: Record<string, string>;
  orientation: string;
  latitude: string;
  longitude: string;
}

export const NO_EDITS: Edits = { fields: {}, orientation: "", latitude: "", longitude: "" };

export function editsFrom(exif: Exif | null): Edits {
  const fields: Record<string, string> = {};
  for (const [key, field] of Object.entries(EDITABLE)) {
    const entry = exif && findEntry(exif.ifds[field.ifd], field.tag);
    fields[key] = !entry ? "" : key === "comment" ? readComment(entry.value, exif.little) : plainText(entry.value);
  }

  const orientation = exif && findEntry(exif.ifds.image, ORIENTATION_TAG);
  const [latitude, longitude] = readLocation(exif);
  return {
    fields,
    orientation: Array.isArray(orientation?.value) && typeof orientation.value[0] === "number"
      ? String(orientation.value[0])
      : "",
    latitude,
    longitude,
  };
}

export function readLocation(exif: Exif | null): [string, string] {
  if (!exif) return ["", ""];
  const latitude = coordinate(
    findEntry(exif.ifds.gps, 2)?.value ?? [],
    plainText(findEntry(exif.ifds.gps, 1)?.value ?? ""),
  );
  const longitude = coordinate(
    findEntry(exif.ifds.gps, 4)?.value ?? [],
    plainText(findEntry(exif.ifds.gps, 3)?.value ?? ""),
  );
  return [latitude === null ? "" : latitude.toFixed(6), longitude === null ? "" : longitude.toFixed(6)];
}

export function sameEdits(left: Edits, right: Edits): boolean {
  if (left.orientation !== right.orientation) return false;
  if (left.latitude !== right.latitude || left.longitude !== right.longitude) return false;
  return Object.keys(EDITABLE).every((key) => (left.fields[key] ?? "") === (right.fields[key] ?? ""));
}

// A box left empty is a tag taken out, which is the only way this page has of removing one and is what somebody
// clearing a name means by it. Both halves of a location go together: half a coordinate is not a place.
export function applyEdits(exif: Exif | null, edits: Edits): Exif {
  const out = exif ? clone(exif) : emptyExif();
  const arrived = editsFrom(exif);

  for (const [key, field] of Object.entries(EDITABLE)) {
    const text = (edits.fields[key] ?? "").trim();
    // A comment can always be written afresh, and has to be: a big-endian file's UTF-16 would land in a little one.
    if (key !== "comment" && text === arrived.fields[key]) continue;
    // A marked box is never written, whatever else is saved beside it: the file keeps what it had there.
    if (problem(key, text) !== null) continue;
    out.ifds[field.ifd] = setEntry(out.ifds[field.ifd], field.tag, text === "" ? null : entryFor(key, field.tag, text));
  }

  out.ifds.image = setEntry(
    out.ifds.image,
    ORIENTATION_TAG,
    edits.orientation === "" ? null : { tag: ORIENTATION_TAG, type: 3, value: [Number(edits.orientation)] },
  );

  const latitude = Number(edits.latitude);
  const longitude = Number(edits.longitude);
  const moved = edits.latitude !== arrived.latitude || edits.longitude !== arrived.longitude;
  // A place nobody moved is not rounded, and one with a marked half is not written: the file keeps the one it had.
  if (!moved || locationProblem(edits.latitude, edits.longitude, true).some(Boolean)) return out;
  if (edits.latitude.trim() === "" && edits.longitude.trim() === "") {
    // Every GPS tag and not only the four that are the coordinate: a timestamp and a bearing are the same place.
    out.ifds.gps = [];
  } else {
    out.ifds.gps = setEntry(out.ifds.gps, 1, { tag: 1, type: 2, value: latitude < 0 ? "S" : "N" });
    out.ifds.gps = setEntry(out.ifds.gps, 2, { tag: 2, type: 5, value: toCoordinate(latitude) });
    out.ifds.gps = setEntry(out.ifds.gps, 3, { tag: 3, type: 2, value: longitude < 0 ? "W" : "E" });
    out.ifds.gps = setEntry(out.ifds.gps, 4, { tag: 4, type: 5, value: toCoordinate(longitude) });
    if (!findEntry(out.ifds.gps, 0)) {
      out.ifds.gps = setEntry(out.ifds.gps, 0, { tag: 0, type: 1, value: Uint8Array.from([2, 3, 0, 0]) });
    }
  }
  return out;
}

// The bytes as they arrived with the metadata block written over, which is the whole of what an edit costs: the
// compressed pixels are never touched, so a JPEG whose caption changed is the same JPEG it was.
export function rewrite(bytes: Uint8Array, container: Container, exif: Exif | null, strip: boolean): Uint8Array | null {
  if (strip) return stripMetadata(bytes, container);
  const block = exif && countEntries(exif) > 0 ? writeExif(exif) : null;
  return writeExifBlock(bytes, container, block);
}

// Judged as it is typed, since a date in the wrong spelling and a latitude past the pole are both wrong the moment
// they exist and a file saved with either is one nothing will read back.
export function problem(key: string, value: string, arrived = ""): string | null {
  const text = value.trim();
  // What the file arrived with is kept as it arrived rather than written, so nothing about it is this page's to judge.
  if (text === "" || text === arrived) return null;
  if (DATE_FIELDS.includes(key) && !DATE_PATTERN.test(text)) return "YYYY:MM:DD HH:MM:SS";
  // An ASCII tag holds seven bits a character, and any byte past that is a guess each reader makes differently.
  const outside = key === "comment" ? null : /[^\x00-\x7f]/u.exec(text);
  if (outside) return `Only ASCII can be written here, and ${outside[0]} is not`;
  return null;
}

// Half a coordinate is a box left blank rather than one typed wrong, so it is only marked once a file is asked for.
export function locationProblem(latitude: string, longitude: string, asked: boolean): [string | null, string | null] {
  const pair: [string | null, string | null] = [null, null];
  const blank = latitude.trim() === "" && longitude.trim() === "";
  if (blank) return pair;
  pair[0] = degreeProblem(latitude, 90, "A latitude runs from -90 to 90", asked);
  pair[1] = degreeProblem(longitude, 180, "A longitude runs from -180 to 180", asked);
  return pair;
}

export interface Marked {
  label: string;
  message: string;
}

// Every box that would stop a save, by the label it is read under, whether or not its message is showing yet.
export function markedBoxes(edits: Edits, arrived: Edits): Marked[] {
  const marked: Marked[] = [];
  for (const [key, field] of Object.entries(EDITABLE)) {
    const message = problem(key, edits.fields[key] ?? "", arrived.fields[key]);
    if (message) marked.push({ label: field.label, message });
  }
  const [latitude, longitude] = locationProblem(edits.latitude, edits.longitude, true);
  if (latitude) marked.push({ label: "Latitude", message: latitude });
  if (longitude) marked.push({ label: "Longitude", message: longitude });
  return marked;
}

// What a save button says in place of the file it did not write.
export function withheldBecause(marked: Marked[]): string {
  const labels = new Intl.ListFormat("en", { type: "conjunction" }).format(marked.map(({ label }) => label));
  return `${labels} ${marked.length === 1 ? "is" : "are"} marked`;
}

function degreeProblem(value: string, limit: number, message: string, asked: boolean): string | null {
  const text = value.trim();
  if (text === "") return asked ? "Both halves of a coordinate, or neither" : null;
  const degrees = Number(text);
  return Number.isFinite(degrees) && Math.abs(degrees) <= limit ? null : message;
}

function entryFor(key: string, tag: number, text: string): ExifEntry {
  // A user comment is not a string field: it is a block of bytes with the character set written in front of it.
  return key === "comment" ? { tag, type: 7, value: writeComment(text) } : { tag, type: 2, value: text };
}

function clone(exif: Exif): Exif {
  const out = emptyExif(exif.little);
  for (const name of IFD_NAMES) out.ifds[name] = [...exif.ifds[name]];
  return out;
}

// The directory a re-encoded picture should carry rather than the one it arrived with: the dimensions are the ones
// it actually is now, and the orientation goes, the canvas having already turned the pixels the tag was asking for.
export function forOutput(exif: Exif, width: number, height: number): Exif {
  const out = clone(exif);
  out.ifds.image = setEntry(out.ifds.image, ORIENTATION_TAG, null);
  out.ifds.exif = setEntry(out.ifds.exif, 40962, { tag: 40962, type: 4, value: [width] });
  out.ifds.exif = setEntry(out.ifds.exif, 40963, { tag: 40963, type: 4, value: [height] });
  // The width and height of IFD0 describe the thumbnail on most files and the picture on some; either way they are
  // now wrong, and a reader that believes them is worse off than one that finds nothing.
  out.ifds.image = setEntry(out.ifds.image, 256, null);
  out.ifds.image = setEntry(out.ifds.image, 257, null);
  return out;
}

// The encoded picture with its metadata put back, where the format it was encoded into can hold any. A blob that
// cannot be rewritten comes back as it was rather than as an error: what was asked for was an image.
export async function withMetadata(blob: Blob, exif: Exif | null, width: number, height: number): Promise<Blob> {
  if (!exif || countEntries(exif) === 0) return blob;
  const bytes = new Uint8Array(await blob.arrayBuffer());
  const container = sniff(bytes);
  if (!carriesExif(container)) return blob;
  const written = writeExifBlock(bytes, container, writeExif(forOutput(exif, width, height)));
  return written ? new Blob([written as BlobPart], { type: blob.type }) : blob;
}
